import React, { useState, useMemo, useContext, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Grid,
  Card,
  Collapse,
  Tooltip,
  useTheme,
  alpha,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Menu,
  MenuItem as DropdownMenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Filter as FilterIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  Scale as ScaleIcon,
  Diamond as DiamondIcon,
  LocalOffer as LocalOfferIcon,
  Receipt as ReceiptIcon,
  Remove as RemoveIcon,
  AttachMoney as MoneyIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  Image as ImageIcon,
  Sell as SellIcon,
  // RemoveShoppingCart as UnsoldIcon,
  DeleteSweep as DeleteSweepIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { DataContext } from '../../context/DataContext';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import '../../styles/ag-grid-premium-theme.css';

const InventoryPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { stock = [], loading, refresh, updateStock, deleteStock, silverStock, silverTransactions, companyDetails, bulkUpdateStock, bulkDeleteStock } = useContext(DataContext);

  // State for master data from company details
  const [itemCategories, setItemCategories] = useState([]);
  const [metalTypes, setMetalTypes] = useState([]);

  // Load master data from company details
  useEffect(() => {
    if (companyDetails && companyDetails.length > 0) {
      const company = companyDetails[0];

      // Load item categories
      if (company.items && Array.isArray(company.items)) {
        setItemCategories(company.items);
      }

      // Load metal types
      if (company.metals && Array.isArray(company.metals)) {
        setMetalTypes(company.metals);
      }
    }
  }, [companyDetails]);

  // Helper function to get item shortcut
  const getItemShortcut = useCallback((itemName) => {
    if (!itemName) return '';
    const item = itemCategories.find(cat =>
      cat.name.toLowerCase() === itemName.toLowerCase()
    );
    return item ? item.shortcut : itemName.substring(0, 3).toUpperCase();
  }, [itemCategories]);

  // Helper function to get metal shortcut
  const getMetalShortcut = useCallback((metalName) => {
    if (!metalName) return '';
    const metal = metalTypes.find(m =>
      m.name.toLowerCase() === metalName.toLowerCase()
    );
    return metal ? metal.shortcut : metalName.substring(0, 2).toUpperCase();
  }, [metalTypes]);

  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  // Silver Transaction Modal State
  const [silverModalOpen, setSilverModalOpen] = useState(false);
  const activeTab = 0;
  const [stockAddData, setStockAddData] = useState([]);
  const [stockSoldData, setStockSoldData] = useState([]);
  const [silverTypeStock, setSilverTypeStock] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    categories: [],
    metals: [],
    soldStatus: []
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [expandedCategories, setExpandedCategories] = useState({});
  const [gridApis, setGridApis] = useState({});
  const debounceTimer = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState(null);
  
  // Bulk action states
  const [bulkActionMenuAnchor, setBulkActionMenuAnchor] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Glassmorphism styles from theme
  const glassStyles = {
    backdrop: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      WebkitBackdropFilter: 'blur(5px)'
    },
    modal: {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '16px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
    },
    card: {
      backgroundColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.8)'
        : 'rgba(30, 41, 59, 0.7)',
      backdropFilter: 'blur(15px)',
      WebkitBackdropFilter: 'blur(15px)',
      border: '1px solid',
      borderColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.4)'
        : 'rgba(148, 163, 184, 0.1)',
      borderRadius: 1,
      boxShadow: theme.shadows[4],
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[5]
      }
    },
    paper: {
      backgroundColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.4)'
        : 'rgba(30, 41, 59, 0.4)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid',
      borderColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(148, 163, 184, 0.1)',
      borderRadius: 2,
      boxShadow: theme.shadows[4]
    },
    button: {
      light: {
        contained: {
          background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
          boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
            boxShadow: '0 6px 20px 0 rgba(37, 99, 235, 0.5)'
          }
        }
      },
      dark: {
        contained: {
          background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
          boxShadow: '0 4px 14px 0 rgba(96, 165, 250, 0.35)',
          '&:hover': {
            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            boxShadow: '0 6px 20px 0 rgba(96, 165, 250, 0.45)'
          }
        }
      }
    }
  };

  // Sorting function for tables
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortData = (data) => {
      console.log(data)
      return [...data].sort((a, b) => {
        if (a[key] < b[key]) {
          return direction === 'asc' ? -1 : 1;
        }
        if (a[key] > b[key]) {
          return direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    };

    if (activeTab === 0) {
      setStockAddData(sortData(stockAddData));
    } else {
      setStockSoldData(sortData(stockSoldData));
    }
  };

  const handlePrintLabel = useCallback((item) => {
    console.log('=== PRINT LABEL DEBUG ===');
    console.log('Item:', item);
    console.log('Item.qr:', item?.qr);
    console.log('Item.serialNo:', item?.serialNo);
    console.log('Item.miscitemName:', item?.miscitemName);
    console.log('Item.miscitemShortcut:', item?.miscitemShortcut);
    console.log('Item.name:', item?.name);

    if (!item?.qr) {
      console.warn('No QR code data found for item');
      setSnackbar({
        open: true,
        message: 'QR code data not found for this item',
        severity: 'warning'
      });
      return;
    }

    const printWindow = window.open('', '_blank');

    // Format the data for the label
    const formatNumber = (num) => {
      if (!num || num === "" || Number.isNaN(num) || num === "NaN") return "0";
      return parseFloat(num).toFixed(3);
    };

    // Get metal short code using imported helper
    const getMetalShort = (metal) => {
      return getMetalShortcut(metal);
    };

    // Get item type short code using imported helper
    const getItemType = (name) => {
      if (!name) return '';

      // First check if it's a Miscellaneous item
      if (name.toLowerCase() === "miscellaneous" && item.miscitemShortcut) {
        return item.miscitemShortcut;
      }

      return getItemShortcut(name);
    };

    function wastageToAlphanumeric(wastage) {
      if (!wastage || wastage === "0" || wastage === "0.0") return "";

      const num = parseFloat(wastage);
      if (isNaN(num)) return "";

      // Handle whole numbers
      if (Number.isInteger(num)) {
        if (num <= 26) {
          // A-Z for 1-26
          return String.fromCharCode(64 + num);
        } else {
          // For numbers > 26, use number as is
          return num.toString();
        }
      }

      // Handle decimal numbers
      const wholePart = Math.floor(num);
      const decimalPart = Math.round((num - wholePart) * 100); // Get two decimal digits

      if (wholePart === 0) {
        // Just decimal part (e.g., 0.9 -> "9")
        return decimalPart.toString();
      }

      if (wholePart <= 26) {
        const letter = String.fromCharCode(64 + wholePart);
        if (decimalPart > 0) {
          return letter + decimalPart.toString();
        }
        return letter;
      } else {
        // For whole part > 26, use number + decimal
        if (decimalPart > 0) {
          return wholePart.toString() + decimalPart.toString();
        }
        return wholePart.toString();
      }
    }

    // Get purity from melting percentage
    const getPurity = (melting) => {
      if (!melting) return '92';
      const purity = Math.round((melting / 100) * 22);
      return purity.toString();
    };

    // Prepare data
    const formattedData = {
      serialNo: item.serialNo || '0',
      name: item.name || '',
      weight: formatNumber(item.weight) || '0',
      grossWeight: formatNumber(item.grossWeight) || '0',
      lessWeight: formatNumber(item.lessWeight) || '0',
      metal: getMetalShort(item.metal),
      huid: item.huid || '',
      makingCharges: item.makingCharges || '',
      wastage: wastageToAlphanumeric(item.wastage) || '0',
      purity: getPurity(item.melting),
      melting: item.melting,
      itemType: getItemType(item.name?.toLowerCase()),
      supplierShortcut: item?.supplierShortcut,
    };

    // Get the QR code directly from item.qr
    const qrData = item.qr || '';

    // Escape any quotes in the QR data for JavaScript
    const escapedQrData = qrData.replace(/"/g, '\\"').replace(/'/g, "\\'");

    const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Jewelry Label - ${item.serialNo}</title>
      <meta charset="UTF-8">
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        @page {
          size: 8.1cm 1.2cm;
          margin: 0 !important;
          padding: 0 !important;
        }
        
        body {
          margin: 0 !important;
          padding: 0 !important;
          width: 8.1cm;
          height: 1.2cm;
          font-family: Arial, sans-serif;
          background: white;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          position: absolute;
          top: 0;
          left: 0;
          overflow: hidden;
        }
        
        .label-container {
          width: 8.1cm;
          height: 1.2cm;
          display: flex;
          position: absolute;
          top: 0;
          left: 0;
        }
        
        .blank-space {
          width: 2.7cm;
          height: 1.2cm;
        }
        
        .label {
          width: 5.4cm;
          height: 1.2cm;
          font-family: Arial, sans-serif;
          font-size: 7px;
          display: flex;
          box-sizing: border-box;
          padding: 0;
          position: relative;
        }
        
        .left,
        .right {
          width: 2.7cm;
          height: 1.2cm;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
          box-sizing: border-box;
          padding-top: 9px !important;
        }
        
        .left {
          padding-left: 3px;
          text-align: left;
        }
        
        .left-inner {
          display: flex;
          flex-direction: row;
          align-items: center;
          width: 100%;
          height: 100%;
        }
        
        .qr-side {
          flex: 0 0 35%;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        
        .text-side {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          padding-left: 2px;
        }
        
        .qr {
          width: 1cm;
          height: 1cm;
          background: #f0f0f0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #999;
          font-size: 5px;
          overflow: hidden;
        }
    
        #qr-canvas {
          width: 100% !important;
          height: 100% !important;
          display: block;
        }
        
        .qr-text {
          font-size: 5px;
          text-align: center;
          line-height: 1;
        }
        
        .shop-name {
          font-size: 9px;
          font-weight: 900;
          color: black;
          text-align: center;
          width: 100%;
          line-height: 1.1;
        }
        
        .making {
          text-align: center;
          width: 100%;
          font-weight: 700;
          font-size: 8px;
          margin-top: 1px;
        }
        
        .item-code {
          font-size: 8px;
          font-weight: 750;
        }
        
        .right {
          padding-left: 8px;
          padding-top: 2px;
        }
        
        .details {
          font-size: 8px;
          font-weight: 750;
        }
        
        .weight {
          font-size: 8px;
          font-weight: 750;
        }
                
        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 8.1cm;
            height: 1.2cm;
          }
          
          body * {
            visibility: visible !important;
          }
          
          .label-container {
            position: absolute;
            top: 0;
            left: 0;
          }
          
          .label {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      </style>
      <script src="https://cdnjs.cloudflare.com/ajax/libs/qrious/4.0.2/qrious.min.js"></script>
    </head>
    <body>
      <div class="label-container">
        <div class="blank-space"></div>
        
        <div class="label">
          <div class="left">
            <div class="left-inner">
              <div class="qr-side">
                <div id="pl-qr" class="qr">
                  <div class="qr-text">Generating QR...</div>
                </div>
              </div>
              <div class="text-side">
                <div class="shop-name">Bhairav Gold</div>
              </div>
            </div>
          </div>
          
          <div class="right">
            <div class="details">
              <span id="itemType">${formattedData.itemType}</span> 
              <span id="itemSrNo">${formattedData.serialNo}</span>
              <span id="itemMetal"> | ${formattedData.metal}</span>
              <span id="itemPurity"> | ${formattedData.melting}</span>
            </div>
            <div class="weight">
              <span id="grossWeightSpan">GW-<span id="itemGrossWeight">${formattedData.grossWeight}</span></span>
              <span id="lessWeightSpan">LW-<span id="itemLessWeight">${formattedData.lessWeight}</span></span>
            </div>
            <div class="weight">NW - <span id="itemWeight">${formattedData.weight}</span></div>
            <div class="item-code">
              <span id="itemHuid">${formattedData.huid}</span> 
            </div>
          </div>
        </div>
      </div>
    
      <script>
        const qrData = "${escapedQrData}";
        
        console.log('QR Data received:', qrData);
        
        if (!qrData || qrData.trim() === '') {
          console.error('No QR data provided');
          document.getElementById('pl-qr').innerHTML = '<div class="qr-text">NO QR DATA</div>';
          setTimeout(printDocument, 500);
        } else {
          function generateQRCode() {
            console.log('Generating QR code using QRious for:', qrData);
            
            const qrContainer = document.getElementById('pl-qr');
            
            try {
              qrContainer.innerHTML = '';
              
              const canvas = document.createElement('canvas');
              canvas.id = 'qr-canvas';
              qrContainer.appendChild(canvas);
              
              if (typeof QRious === 'undefined') {
                throw new Error('QRious library not loaded');
              }
              
              const qr = new QRious({
                element: canvas,
                value: qrData,
                size: 180,
                level: 'H',
                background: 'white',
                foreground: 'black',
                backgroundAlpha: 1,
                foregroundAlpha: 1,
                padding: 2
              });
              
              console.log('QR Code generated successfully');
              
              canvas.style.width = '100%';
              canvas.style.height = '100%';
              
              setTimeout(printDocument, 500);
              
            } catch (error) {
              console.error('QR generation failed:', error);
              qrContainer.innerHTML = '<div class="qr-text" style="font-size: 5px;">QR: ' + 
                                      qrData.substring(0, 8) + 
                                      (qrData.length > 8 ? '...' : '') + '</div>';
              setTimeout(printDocument, 500);
            }
          }
          
          function generateSimpleQRCode() {
            const qrContainer = document.getElementById('pl-qr');
            qrContainer.innerHTML = '';
            
            const canvas = document.createElement('canvas');
            canvas.width = 60;
            canvas.height = 60;
            canvas.style.width = '100%';
            canvas.style.height = '100%';
            
            const ctx = canvas.getContext('2d');
            
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, 60, 60);
            
            ctx.fillStyle = '#000000';
            
            ctx.fillRect(4, 4, 10, 10);
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(6, 6, 6, 6);
            ctx.fillStyle = '#000000';
            
            ctx.fillRect(46, 4, 10, 10);
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(48, 6, 6, 6);
            ctx.fillStyle = '#000000';
            
            ctx.fillRect(4, 46, 10, 10);
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(6, 48, 6, 6);
            ctx.fillStyle = '#000000';
            
            ctx.fillStyle = '#000000';
            ctx.font = '6px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('QR', 30, 26);
            ctx.fillText('Code', 30, 34);
            
            qrContainer.appendChild(canvas);
            console.log('Simple QR placeholder created');
          }
          
          function printDocument() {
            console.log('Printing document...');
            
            window.print();
            
            setTimeout(() => {
              window.close();
            }, 1000);
          }
          
          function init() {
            if (typeof QRious !== 'undefined') {
              console.log('QRious library is loaded');
              generateQRCode();
            } else {
              console.log('Waiting for QRious library to load...');
              let attempts = 0;
              const maxAttempts = 10;
              
              function checkLibrary() {
                attempts++;
                if (typeof QRious !== 'undefined') {
                  console.log('QRious loaded after', attempts, 'attempts');
                  generateQRCode();
                } else if (attempts < maxAttempts) {
                  setTimeout(checkLibrary, 100);
                } else {
                  console.error('QRious library failed to load');
                  generateSimpleQRCode();
                  setTimeout(printDocument, 500);
                }
              }
              
              checkLibrary();
            }
          }
          
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init);
          } else {
            init();
          }
        }
      </script>
    </body>
    </html>
  `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();

    printWindow.focus();

    setSnackbar({
      open: true,
      message: `Label for ${item.serialNo || item.name} printed successfully`,
      severity: 'success'
    });
  }, [getItemShortcut, getMetalShortcut]);

  const handleDelete = useCallback(async (item) => {
    if (window.confirm(`Are you sure you want to delete item ${item.serialNo || item.name}?`)) {
      setIsProcessingAction(true);
      
      try {
        const response = await deleteStock(item.id);
        console.log('Delete response:', response);
        
        if (!response?.success) {
          throw new Error('Failed to delete item');
        }
        
        setSnackbar({
          open: true,
          message: `Item ${item.serialNo} deleted successfully`,
          severity: 'success'
        });
        
        await refresh();
        
      } catch (error) {
        console.error('Delete failed:', error);
        setSnackbar({
          open: true,
          message: `Failed to delete: ${error.message}`,
          severity: 'error'
        });
      } finally {
        setIsProcessingAction(false);
      }
    }
  },[ deleteStock, refresh]);

  const handleEdit = useCallback((item) => {
    setEditingItem(item);
    setEditFormData({ ...item });
    setEditDialogOpen(true);
  },[]);

  // Bulk action handlers
  const handleBulkActionClick = (event) => {
    setBulkActionMenuAnchor(event.currentTarget);
  };

  const handleBulkActionClose = () => {
    setBulkActionMenuAnchor(null);
  };

  const executeBulkAction = async (action) => {
    if (selectedItems.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select items to perform this action',
        severity: 'warning'
      });
      return;
    }
    
    handleBulkActionClose();
    
    if (action === 'delete') {
      if (!window.confirm(`Are you sure you want to delete ${selectedItems.length} item(s)? This action cannot be undone.`)) {
        return;
      }
    }
    
    setBulkActionLoading(true);
    
    const selectedItemsData = filteredData.filter(item => selectedItems.includes(item.id));
    
    try {
      let result;
      let successMessage = '';
      
      if (action === 'markAsSold') {
        result = await bulkUpdateStock(selectedItemsData, 'markAsSold');
        successMessage = `${selectedItemsData.length} item(s) marked as sold successfully`;
      } else if (action === 'markAsUnsold') {
        result = await bulkUpdateStock(selectedItemsData, 'markAsUnsold');
        successMessage = `${selectedItemsData.length} item(s) marked as unsold successfully`;
      } else if (action === 'delete') {
        result = await bulkDeleteStock(selectedItemsData);
        successMessage = `${selectedItemsData.length} item(s) deleted successfully`;
      }
      
      if (result?.success) {
        setSnackbar({
          open: true,
          message: successMessage,
          severity: 'success'
        });
        
        // Clear selection
        setSelectedItems([]);
        Object.values(gridApis).forEach(api => {
          if (api) {
            api.deselectAll();
          }
        });
        
        // Refresh to ensure sync
        await refresh();
      } else {
        throw new Error(result?.message || result?.error || 'Bulk action failed');
      }
      
    } catch (error) {
      console.error('Bulk action failed:', error);
      setSnackbar({
        open: true,
        message: `Failed: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  // AG Grid column definitions
  const columnDefs = useMemo(() => [
    {
      headerName: 'SERIAL NO',
      field: 'serialNo',
      width: 140,
      sortable: true,
      sort: "asc",
      filter: 'agTextColumnFilter',
      cellStyle: {
        fontWeight: '600',
        color: theme.palette.text.primary,
        fontSize: '14px'
      },
      comparator: (valueA, valueB) => {
        return (parseInt(valueA) || 0) - (parseInt(valueB) || 0);
      }
    },
    {
      headerName: 'IMAGE',
      field: 'imageUrl',
      width: 100,
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        if (!params.value) {
          return (
            <Box
              sx={{
                width: 50,
                height: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha(theme.palette.text.disabled, 0.1),
                borderRadius: '8px',
                border: `1px dashed ${alpha(theme.palette.text.disabled, 0.3)}`
              }}
            >
              <ImageIcon sx={{ color: 'text.disabled', fontSize: 24 }} />
            </Box>
          );
        }

        const getOptimizedImageUrl = (url) => {
          if (!url) return url;
          return url.replace('/upload/', '/upload/q_auto,f_auto/w_50,h_50,c_fill/');
        };

        return (
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '8px',
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: theme.shadows[3]
              }
            }}
            onClick={() => {
              if (params.value) {
                window.open(params.value, '_blank');
              }
            }}
          >
            <img
              src={getOptimizedImageUrl(params.value)}
              alt="Product"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div style="width:50px;height:50px;display:flex;align-items:center;justify-content:center;background:#f0f0f0;border-radius:8px;"><svg>...</svg></div>';
              }}
            />
          </Box>
        );
      },
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px'
      }
    },
    {
      headerName: 'NAME',
      field: 'name',
      width: 140,
      sortable: true,
      filter: 'agTextColumnFilter',
      cellStyle: {
        fontWeight: '600',
        color: theme.palette.text.primary,
        fontSize: '14px'
      },
      comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
        const valA = nodeA.data?.name || '';
        const valB = nodeB.data?.name || '';

        if (valA === 'Miscellaneous' && valB === 'Miscellaneous') {
          const miscNameA = nodeA.data?.miscitemName || '';
          const miscNameB = nodeB.data?.miscitemName || '';
          return miscNameA.localeCompare(miscNameB);
        }

        return valA.localeCompare(valB);
      },
      valueGetter: (params) => {
        const name = params.data?.name || '';
        const miscitemName = params.data?.miscitemName || '';
        const miscitemShortcut = params.data?.miscitemShortcut || params.data?.itemShortcut || '';

        if (name === 'Miscellaneous' && miscitemName) {
          if (miscitemShortcut) {
            return `Miscellaneous (${miscitemName} - ${miscitemShortcut})`;
          }
          return `Miscellaneous (${miscitemName})`;
        }

        return name;
      }
    },
    {
      headerName: 'GROSS WEIGHT',
      field: 'grossWeight',
      width: 160,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null || params.value === "") return '-';
        return `${parseFloat(params.value).toFixed(3)} gm`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px'
      }
    },
    {
      headerName: 'LESS WEIGHT',
      field: 'lessWeight',
      width: 160,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null || params.value === "" || params.value === "NaN") return '-';
        return `${parseFloat(params.value).toFixed(3)} gm`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px'
      }
    },
    {
      headerName: 'WEIGHT',
      field: 'weight',
      width: 140,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '-';
        return `${parseFloat(params.value).toFixed(3)} gm`;
      },
      cellStyle: {
        fontWeight: '700',
        color: theme.palette.primary.main,
        fontSize: '14px'
      },
      comparator: (valueA, valueB) => {
        return parseFloat(valueA || 0) - parseFloat(valueB || 0);
      }
    },
    {
      headerName: 'METAL',
      field: 'metal',
      width: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        if (!params.value) return '-';
        const metal = metalTypes.find(m => m.name === params.value);
        const shortcut = metal ? metal.shortcut : '';
        const isGold = params.value === 'Gold';
        return (
          <Chip
            label={shortcut ? `${params.value} (${shortcut})` : params.value}
            size="small"
            sx={{
              backgroundColor: isGold
                ? alpha('#f59e0b', 0.15)
                : alpha(theme.palette.secondary.main, 0.15),
              color: isGold ? '#92400e' : theme.palette.secondary.main,
              fontWeight: 600,
              border: `1px solid ${isGold ? '#fbbf24' : theme.palette.secondary.main}`,
              borderRadius: '8px',
              fontSize: '12px',
              height: '24px',
              backdropFilter: 'blur(10px)'
            }}
          />
        );
      }
    },
    {
      headerName: 'HUID',
      field: 'huid',
      width: 160,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        if (!params.value) return '-';
        return (
          <Chip
            label={params.value}
            size="small"
            variant="outlined"
            sx={{
              color: theme.palette.mode === 'light' ? '#8B5CF6' : '#A78BFA',
              borderColor: theme.palette.mode === 'light' ? '#8B5CF6' : '#A78BFA',
              borderRadius: '8px',
              fontSize: '12px',
              height: '24px',
              backdropFilter: 'blur(10px)'
            }}
          />
        );
      }
    },
    {
      headerName: 'STATUS',
      field: 'sold',
      width: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params) => {
        const isSold = params.value === "Yes";
        return (
          <Chip
            label={isSold ? "Sold" : "Available"}
            size="small"
            sx={{
              backgroundColor: isSold
                ? alpha('#10b981', 0.15)
                : alpha('#f59e0b', 0.15),
              color: isSold ? '#065f46' : '#92400e',
              fontWeight: 600,
              border: `1px solid ${isSold ? '#10b981' : '#f59e0b'}`,
              borderRadius: '8px',
              fontSize: '12px',
              height: '24px',
              backdropFilter: 'blur(10px)'
            }}
          />
        );
      }
    },
    {
      headerName: 'SUPPLIER',
      field: 'supplierName',
      width: 200,
      sortable: true,
      filter: true,
      cellStyle: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: theme.palette.text.primary,
        fontSize: '14px'
      }
    },
    {
      headerName: 'MELTING %',
      field: 'melting',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '-';
        return `${params.value}%`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px',
        fontWeight: '600'
      }
    },
    {
      headerName: 'SILVER RATE',
      field: 'silverRate',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null || params.value === "") return '-';
        return `${params.value}`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px',
        fontWeight: '600'
      }
    },
    {
      headerName: 'LABOUR',
      field: 'labour',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null || params.value === "") return '-';
        return `${params.value}`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px',
        fontWeight: '600'
      }
    },
    {
      headerName: 'GOLD RATE',
      field: 'goldCuttingRate',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '-';
        return `${params.value}`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px',
        fontWeight: '600'
      }
    },
    {
      headerName: 'WASTAGE %',
      field: 'wastage',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '-';
        return `${params.value}%`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px',
        fontWeight: '600'
      }
    },
    {
      headerName: 'MAKING CHARGE',
      field: 'makingCharges',
      width: 130,
      sortable: true,
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => {
        if (params.value === undefined || params.value === null) return '-';
        return `${params.value}%`;
      },
      cellStyle: {
        color: theme.palette.text.primary,
        fontSize: '14px',
        fontWeight: '600'
      }
    },
    {
      headerName: 'ACTIONS',
      field: 'actions',
      width: 200,
      pinned: 'right',
      suppressMovable: true,
      cellRenderer: (params) => (
        <Box className="flex space-x-2">
          <Tooltip title="Edit" arrow>
            <IconButton
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                borderRadius: '12px',
                padding: '6px',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(10px)'
              }}
              onClick={() => handleEdit(params.data)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <IconButton
              size="small"
              sx={{
                backgroundColor: alpha('#ef4444', 0.1),
                color: '#ef4444',
                borderRadius: '12px',
                padding: '6px',
                '&:hover': {
                  backgroundColor: alpha('#ef4444', 0.2),
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(10px)'
              }}
              onClick={() => handleDelete(params.data)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print Label" arrow>
            <IconButton
              size="small"
              sx={{
                backgroundColor: alpha('#f59e0b', 0.1),
                color: '#f59e0b',
                borderRadius: '12px',
                padding: '6px',
                '&:hover': {
                  backgroundColor: alpha('#f59e0b', 0.2),
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(10px)'
              }}
              onClick={() => handlePrintLabel(params.data)}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ], [theme, metalTypes, handlePrintLabel, handleDelete, handleEdit]);

  // AG Grid default column properties
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 120,
    suppressMenu: false,
    floatingFilter: false,
    wrapText: true,
    autoHeight: false,
    cellStyle: {
      color: theme.palette.text.primary,
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center'
    },
    headerClass: 'ag-header-cell-premium'
  }), [theme.palette.text.primary]);

  // Handle search with debounce
  const handleSearch = useCallback((value) => {
    setSearchTerm(value);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      // Search logic is handled in filteredData memo
    }, 300);
  }, []);

  const filteredData = useMemo(() => {
    if (!stock.length) return [];

    let result = stock;
    const searchLower = searchTerm.toLowerCase();

    // Apply search filter
    if (searchTerm) {
      result = result.filter(item => {
        if (item.serialNo && item.serialNo.toString().toLowerCase().includes(searchLower)) return true;
        if (item.name && item.name.toString().toLowerCase().includes(searchLower)) return true;
        if (item.huid && item.huid.toString().toLowerCase().includes(searchLower)) return true;
        if (item.description && item.description.toLowerCase().includes(searchLower)) return true;
        if (item.supplierName && item.supplierName.toLowerCase().includes(searchLower)) return true;
        if (item.weight && item.weight.toString().toLowerCase().includes(searchLower)) return true;
        if (item.lessWeight && item.lessWeight.toString().toLowerCase().includes(searchLower)) return true;
        if (item.grossWeight && item.grossWeight.toString().toLowerCase().includes(searchLower)) return true;
        return false;
      });
    }

    // Apply category filters
    if (filters.categories.length > 0) {
      result = result.filter(item => filters.categories.includes(item.name));
    }

    // Apply metal filters
    if (filters.metals.length > 0) {
      result = result.filter(item => filters.metals.includes(item.metal));
    }

    // Apply sold status filters
    if (filters.soldStatus.length > 0) {
      result = result.filter(item => {
        const isSold = item.sold === "Yes";
        return (filters.soldStatus.includes('sold') && isSold) ||
          (filters.soldStatus.includes('unsold') && !isSold);
      });
    }

    return result;
  }, [stock, searchTerm, filters]);

  // Calculate statistics - optimized
  const statistics = useMemo(() => {
    if (!stock.length) {
      return {
        totalItems: 0,
        soldItems: 0,
        unsoldItems: 0,
        totalWeight: '0.000',
        totalFine: '0.000'
      };
    }

    const totalItems = filteredData.length;
    const soldItems = filteredData.filter(item => item.sold === "Yes").length;
    const unsoldItems = totalItems - soldItems;

    let totalWeight = 0;
    let totalFine = 0;

    for (let i = 0; i < filteredData.length; i++) {
      const item = filteredData[i];
      const weight = parseFloat(item.weight) || 0;
      const melting = parseFloat(item.melting) || 0;
      const wastage = parseFloat(item.wastage) || 0;

      totalWeight += weight;
      totalFine += weight * ((melting + wastage) / 100);
    }

    return {
      totalItems,
      soldItems,
      unsoldItems,
      totalWeight: totalWeight.toFixed(2),
      totalFine: totalFine.toFixed(2)
    };
  }, [filteredData, stock.length]);

  // Calculate category counts - optimized
  const categoryCounts = useMemo(() => {
    return stock.reduce((counts, item) => {
      const category = item.name || 'Uncategorized';
      counts[category] = (counts[category] || 0) + 1;
      return counts;
    }, {});
  }, [stock]);

  // Calculate metal counts - optimized
  const metalCounts = useMemo(() => {
    return stock.reduce((counts, item) => {
      const metal = item.metal || 'Unknown';
      counts[metal] = (counts[metal] || 0) + 1;
      return counts;
    }, {});
  }, [stock]);

  // Sold status counts
  const soldStatusCounts = useMemo(() => {
    return stock.reduce((acc, item) => {
      if (item.sold === "Yes") {
        acc.sold++;
      } else {
        acc.unsold++;
      }
      return acc;
    }, { sold: 0, unsold: 0 });
  }, [stock]);

  // Extract unique categories and metals - simplified
  const categories = useMemo(() => Object.keys(categoryCounts).sort(), [categoryCounts]);
  const metals = useMemo(() => Object.keys(metalCounts).sort(), [metalCounts]);

  // Group filtered data by category
  const filteredGroupedData = useMemo(() => {
    if (filteredData.length === 0) return {};

    return filteredData.reduce((groups, item) => {
      const category = item.name || 'Uncategorized';

      if (!groups[category]) {
        groups[category] = [];
      }

      groups[category].push(item);
      return groups;
    }, {});
  }, [filteredData]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...prev[filterType], value]
    }));
  };

  // Handle category expansion
  const handleCategoryToggle = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));

    setTimeout(() => {
      if (gridApis[category]) {
        gridApis[category].sizeColumnsToFit();
      }
    }, 100);
  };

  useEffect(() => {
    console.log('Silver Stock:', silverStock);
    console.log('Silver Transactions:', silverTransactions, silverTransactions ? silverTransactions.length : 0);

    setSilverTypeStock(silverStock)

    if (silverTransactions && silverTransactions.length > 0) {
      console.log('Processing silver transactions...', silverTransactions);
      const addData = silverTransactions.filter(item => item?.silverTransaction?.[0]?.operation === "add");
      const soldData = silverTransactions.filter(item => item?.silverTransaction?.[0]?.operation === "subtract");

      const transformedAddData = addData.map(item => ({
        id: item.id || Math.random().toString(36).substr(2, 9),
        date: new Date(item.date || item.timestamp).toLocaleDateString('en-GB'),
        silverType: item?.silverTransaction?.[0]?.silverType,
        netWeight: item?.silverTransaction?.[0]?.netWeight,
        supplierName: item?.silverTransaction?.[0]?.description || "Unknown Supplier"
      }));

      const transformedSoldData = soldData.map(item => ({
        id: item.id || Math.random().toString(36).substr(2, 9),
        date: new Date(item.date || item.timestamp).toLocaleDateString('en-GB'),
        silverType: item?.silverTransaction?.[0]?.silverType,
        netWeight: item?.silverTransaction?.[0]?.netWeight,
        billNo: item?.silverTransaction?.[0]?.description || "N/A",
        customerName: item?.silverTransaction?.[0]?.customerName || "Unknown Customer",
        itemName: item?.silverTransaction?.[0]?.itemName || item?.silverTransaction?.[0]?.description || "Unknown Item"
      }));

      console.log('Transformed Add Data:', transformedAddData);
      console.log('Transformed Sold Data:', transformedSoldData);

      setStockAddData(transformedAddData);
      setStockSoldData(transformedSoldData);
    } else {
      setStockAddData([]);
      setStockSoldData([]);
    }
  }, [silverTransactions, silverStock]);

  // Handle grid ready event
  const handleGridReady = (category, params) => {
    setGridApis(prev => ({
      ...prev,
      [category]: params.api
    }));
    params.api.sizeColumnsToFit();

    const selectedIds = new Set(selectedItems);
    params.api.forEachNode(node => {
      node.setSelected(selectedIds.has(node.data.id));
    });
  };

  // Handle grid selection change
  const handleSelectionChanged = (category, items) => {
    return (params) => {
      const selectedRows = params.api.getSelectedRows();
      const selectedIds = selectedRows.map(row => row.id);
      const allItemIds = items.map(item => item.id);

      setSelectedItems(prev => {
        const newSelected = new Set(prev);

        selectedIds.forEach(id => newSelected.add(id));

        allItemIds.forEach(id => {
          if (!selectedIds.includes(id)) {
            newSelected.delete(id);
          }
        });

        return Array.from(newSelected);
      });
    };
  };

  const handleSave = async (itemId) => {
    setIsProcessingAction(true);
    
    try {
      console.log('Executing update for item:', itemId);
      
      const response = await updateStock(itemId, editFormData);
      console.log('Update response:', response);
      
      if (!response?.success) {
        throw new Error('Failed to update item');
      }
      
      setSnackbar({
        open: true,
        message: 'Item updated successfully',
        severity: 'success'
      });
      
      setEditDialogOpen(false);
      setEditingItem(null);
      setEditFormData({});
      
      await refresh();
      
    } catch (error) {
      console.error('Update failed:', error);
      setSnackbar({
        open: true,
        message: `Failed to update: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsProcessingAction(false);
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingItem(null);
    setEditFormData({});

    setSnackbar({
      open: true,
      message: 'Edit cancelled',
      severity: 'info'
    });
  };

  // Export function - matches HTML exactly
  const handleExport = () => {
    if (filteredData.length === 0) {
      setSnackbar({
        open: true,
        message: 'No data to export',
        severity: 'warning'
      });
      return;
    }

    try {
      const sortedData = [...filteredData].sort((a, b) => {
        if ((a.name || '').localeCompare(b.name || '') !== 0) {
          return (a.name || '').localeCompare(b.name || '');
        }
        return (parseInt(a.serialNo) || 0) - (parseInt(b.serialNo) || 0);
      });

      const headers = [
        'Serial No.', 'Name', 'Weight', 'Gross Weight', 'Less Weight',
        'Buying Rate', 'Gold Cutting Rate', 'Wastage', 'Supplier Name',
        'Metal', 'Melting', 'HUID', 'Sold', 'Description', 'Item Detail'
      ];

      let csvRows = [];
      let currentCategory = '';

      sortedData.forEach(item => {
        if (item.name !== currentCategory) {
          csvRows.push([`${item.name} (Category)`]);
          csvRows.push(headers);
          currentCategory = item.name;
        }

        csvRows.push([
          item.serialNo || '',
          item.name || '',
          item.weight || '',
          item.grossWeight || '',
          item.lessWeight || '',
          item.buyingRate || '',
          item.goldCuttingRate || '',
          item.wastage || '',
          item.supplierName || '',
          item.metal || '',
          item.melting || '',
          item.huid || '',
          item.sold || '',
          item.description || '',
          item.itemDetail || ''
        ]);
      });

      const csvContent = csvRows
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `jewelry_inventory_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'Data exported successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Export error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to export data',
        severity: 'error'
      });
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    try {
      await refresh();
      setSnackbar({
        open: true,
        message: 'Data refreshed successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to refresh data',
        severity: 'error'
      });
    }
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({
      categories: [],
      metals: [],
      soldStatus: []
    });
    setSelectedItems([]);

    Object.values(gridApis).forEach(api => {
      if (api) {
        api.deselectAll();
      }
    });
  };

  // Select all items across all categories
  const handleSelectAll = () => {
    const allIds = filteredData.map(item => item.id);
    setSelectedItems(allIds);

    Object.values(gridApis).forEach(api => {
      if (api) {
        api.selectAll();
      }
    });
  };

  // Clear all selections
  const handleClearSelection = () => {
    setSelectedItems([]);

    Object.values(gridApis).forEach(api => {
      if (api) {
        api.deselectAll();
      }
    });
  };

  return (
    <Box className="min-h-screen">
      <Container maxWidth="xl" className="py-8">
        <Grid container spacing={3}>
          {/* Filters Sidebar */}
          <Grid item size={{ xs: 12, md: 3 }}>
            <Paper
              sx={{
                ...glassStyles.paper,
                padding: '24px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <Box className="flex items-center justify-between mb-4">
                <Typography
                  variant="h6"
                  className="font-bold flex items-center"
                  sx={{ color: 'text.primary' }}
                >
                  <FilterIcon className="mr-2" />
                  Filters
                </Typography>
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                  sx={{
                    color: 'text.secondary',
                    backgroundColor: alpha(theme.palette.text.secondary, 0.1),
                    borderRadius: '12px',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.text.secondary, 0.2)
                    }
                  }}
                >
                  Clear
                </Button>
              </Box>

              {/* Selection Actions */}
              {selectedItems.length > 0 && (
                <Box
                  className="mb-4 p-3 rounded-lg"
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    borderRadius: '12px'
                  }}
                >
                  <Typography variant="subtitle2" className="font-semibold mb-2" sx={{ color: 'primary.main' }}>
                    {selectedItems.length} items selected
                  </Typography>
                  <Box className="flex space-x-2">
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{
                        color: 'primary.main',
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        borderRadius: '8px',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: alpha(theme.palette.primary.main, 0.1)
                        }
                      }}
                      onClick={handleClearSelection}
                    >
                      Clear
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        borderRadius: '8px',
                        '&:hover': {
                          boxShadow: theme.shadows[4]
                        }
                      }}
                      onClick={handleSelectAll}
                    >
                      Select All ({filteredData.length})
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Search Bar */}
              <Box className="mb-6">
                <TextField
                  fullWidth
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    sx: {
                      backgroundColor: alpha(theme.palette.text.primary, 0.05),
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.text.primary, 0.2)
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.3)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: '2px'
                      }
                    }
                  }}
                />
              </Box>

              {/* Category Filter */}
              <Box className="mb-6">
                <Typography
                  variant="subtitle2"
                  className="font-semibold mb-2"
                  sx={{
                    color: 'text.primary',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    paddingBottom: '8px'
                  }}
                >
                  📁 CATEGORIES
                </Typography>
                <FormGroup>
                  {categories.map((category) => (
                    <FormControlLabel
                      key={category}
                      control={
                        <Checkbox
                          size="small"
                          checked={filters.categories.includes(category)}
                          onChange={() => handleFilterChange('categories', category)}
                          sx={{
                            color: 'primary.main',
                            '&.Mui-checked': {
                              color: 'primary.main',
                            }
                          }}
                        />
                      }
                      label={
                        <Box
                          className="flex items-center justify-between w-full"
                          sx={{ width: "100%" }}
                        >
                          <span
                            className="truncate max-w-[120px]"
                            style={{ color: theme.palette.text.primary }}
                          >
                            {category}
                            {category !== 'Miscellaneous' &&
                              ` (${getItemShortcut(category)})`}
                          </span>

                          <Chip
                            size="small"
                            label={categoryCounts[category] || 0}
                            sx={{
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              color: theme?.palette?.mode === 'dark' ? '#c5dbe0' : '#2558a8',
                              fontWeight: 600,
                              borderRadius: '8px',
                              backdropFilter: 'blur(10px)',
                            }}
                          />
                        </Box>
                      }
                      sx={{
                        marginBottom: "4px",
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "100%",
                        }
                      }}
                    />
                  ))}
                </FormGroup>
              </Box>

              {/* Metal Filter */}
              <Box className="mb-6">
                <Typography
                  variant="subtitle2"
                  className="font-semibold mb-2"
                  sx={{
                    color: 'text.primary',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    paddingBottom: '8px'
                  }}
                >
                  💎 METAL TYPE
                </Typography>
                <FormGroup>
                  {metals.map((metal) => (
                    <FormControlLabel
                      key={metal}
                      control={
                        <Checkbox
                          size="small"
                          checked={filters.metals.includes(metal)}
                          onChange={() => handleFilterChange('metals', metal)}
                          sx={{
                            color: 'secondary.main',
                            '&.Mui-checked': {
                              color: 'secondary.main',
                            }
                          }}
                        />
                      }
                      label={
                        <Box className="flex justify-between w-full" sx={{ width: "100%" }}>
                          <span style={{ color: theme.palette.text.primary }}>
                            {metal} ({getMetalShortcut(metal)})
                          </span>
                          <Chip
                            size="small"
                            label={metalCounts[metal] || 0}
                            sx={{
                              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                              color: 'secondary.main',
                              fontWeight: 600,
                              borderRadius: '8px',
                              backdropFilter: 'blur(10px)'
                            }}
                          />
                        </Box>
                      }
                      sx={{
                        marginBottom: "4px",
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "100%",
                        }
                      }}
                    />
                  ))}
                </FormGroup>
              </Box>

              {/* Sold Status Filter */}
              <Box className="mb-6">
                <Typography
                  variant="subtitle2"
                  className="font-semibold mb-2"
                  sx={{
                    color: 'text.primary',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    paddingBottom: '8px'
                  }}
                >
                  📊 SOLD STATUS
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={filters.soldStatus.includes('sold')}
                        onChange={() => handleFilterChange('soldStatus', 'sold')}
                        sx={{
                          color: '#10b981',
                          '&.Mui-checked': {
                            color: '#10b981',
                          }
                        }}
                      />
                    }
                    label={
                      <Box className="flex justify-between w-full">
                        <span style={{ color: theme.palette.text.primary }}>Sold</span>
                        <Chip
                          size="small"
                          label={soldStatusCounts.sold}
                          sx={{
                            backgroundColor: alpha('#10b981', 0.1),
                            color: '#10b981',
                            fontWeight: 600,
                            borderRadius: '8px',
                            backdropFilter: 'blur(10px)'
                          }}
                        />
                      </Box>
                    }
                    sx={{
                      marginBottom: "4px",
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        width: "100%",
                      }
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={filters.soldStatus.includes('unsold')}
                        onChange={() => handleFilterChange('soldStatus', 'unsold')}
                        sx={{
                          color: '#f59e0b',
                          '&.Mui-checked': {
                            color: '#f59e0b',
                          }
                        }}
                      />
                    }
                    label={
                      <Box className="flex justify-between w-full">
                        <span style={{ color: theme.palette.text.primary }}>Not Sold</span>
                        <Chip
                          size="small"
                          label={soldStatusCounts.unsold}
                          sx={{
                            backgroundColor: alpha('#f59e0b', 0.1),
                            color: '#f59e0b',
                            fontWeight: 600,
                            borderRadius: '8px',
                            backdropFilter: 'blur(10px)'
                          }}
                        />
                      </Box>
                    }
                    sx={{
                      marginBottom: "4px",
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        width: "100%",
                      }
                    }}
                  />
                </FormGroup>
              </Box>
            </Paper>
          </Grid>

          {/* Main Content */}
          <Grid item size={{ xs: 12, md: 9 }}>
            <Paper
              sx={{
                ...glassStyles.paper,
                padding: '25px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {/* Header */}
              <Box className="mb-8">
                <Box className="flex items-center justify-between">
                  <Box className="flex items-center space-x-3">
                    <Box
                      sx={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        boxShadow: theme.shadows[3]
                      }}
                    >
                      <InventoryIcon sx={{ color: 'white', fontSize: '28px' }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" className="font-bold" sx={{ color: 'text.primary' }}>
                        Inventory Dashboard
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Manage and monitor your jewelry inventory
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ReceiptIcon />}
                    onClick={() => {
                      setSilverModalOpen(true);
                    }}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                      borderRadius: '10px',
                      px: 3,
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      boxShadow: theme.shadows[3],
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`,
                        boxShadow: theme.shadows[5],
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Gross Stock Details
                  </Button>
                </Box>
              </Box>

              {/* Statistics Cards */}
              <Grid container spacing={1.5} className="mb-8">
                {[
                  {
                    title: 'TOTAL ITEMS',
                    value: statistics.totalItems,
                    icon: <InventoryIcon />,
                    gradient: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)'
                  },
                  {
                    title: 'SOLD ITEMS',
                    value: statistics.soldItems,
                    icon: <TrendingUpIcon />,
                    gradient: 'linear-gradient(135deg, #10b981 0%, #047857 100%)'
                  },
                  {
                    title: 'UNSOLD ITEMS',
                    value: statistics.unsoldItems,
                    icon: <LocalOfferIcon />,
                    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                  },
                  {
                    title: 'TOTAL WEIGHT',
                    value: `${statistics.totalWeight} gm`,
                    icon: <ScaleIcon />,
                    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                  },
                  {
                    title: 'TOTAL FINE',
                    value: `${statistics.totalFine} gm`,
                    icon: <DiamondIcon />,
                    gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)'
                  }
                ].map((stat, index) => (
                  <Grid item xs={12} sm={6} md={2.4} key={index}>
                    <Card
                      sx={{
                        ...glassStyles.card,
                        padding: '7px',
                        height: '100%'
                      }}
                    >
                      <Box className="flex items-center justify-between">
                        <Box
                          sx={{
                            width: '35px',
                            height: '35px',
                            mr: 1,
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: stat.gradient,
                            boxShadow: theme.shadows[2]
                          }}
                        >
                          {React.cloneElement(stat.icon, { sx: { color: 'white', fontSize: '20px' } })}
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            className="font-semibold uppercase tracking-wider"
                            sx={{ color: 'text.secondary' }}
                          >
                            {stat.title}
                          </Typography>
                          <Typography
                            variant="h5"
                            className="font-bold mt-2"
                            sx={{ color: 'text.primary' }}
                          >
                            {stat.value}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Toolbar */}
              <Box className="flex flex-wrap items-center justify-between mb-6 gap-3">
                <Box className="flex items-center space-x-3">
                  <Typography variant="h5" className="font-bold" sx={{ color: 'text.primary' }}>
                    Inventory Items
                  </Typography>
                  <Chip
                    label={`${filteredData.length} items`}
                    size="small"
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                      fontWeight: 600,
                      borderRadius: '8px',
                      backdropFilter: 'blur(10px)'
                    }}
                  />
                  {selectedItems.length > 0 && (
                    <Chip
                      label={`${selectedItems.length} selected`}
                      size="small"
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        color: 'white',
                        borderRadius: '8px',
                        backdropFilter: 'blur(10px)'
                      }}
                    />
                  )}
                  {loading && <CircularProgress size={20} sx={{ color: 'primary.main' }} />}
                </Box>

                <Box className="flex flex-wrap gap-2">
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                      ...(theme.palette.mode === 'light'
                        ? glassStyles.button.light.contained
                        : glassStyles.button.dark.contained),
                      borderRadius: '12px',
                      padding: '10px 20px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                    onClick={() => navigate('/addItem')}
                  >
                    Add New Item
                  </Button>

                  {/* Bulk Actions Button */}
                  {selectedItems.length > 0 && (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<MoreVertIcon />}
                        onClick={handleBulkActionClick}
                        sx={{
                          background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                          borderRadius: '12px',
                          padding: '10px 20px',
                          textTransform: 'none',
                          fontWeight: 600,
                          '&:hover': {
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      >
                        Bulk Actions ({selectedItems.length})
                      </Button>

                      <Menu
                        anchorEl={bulkActionMenuAnchor}
                        open={Boolean(bulkActionMenuAnchor)}
                        onClose={handleBulkActionClose}
                        PaperProps={{
                          sx: {
                            backgroundColor: theme.palette.mode === 'light'
                              ? 'rgba(255, 255, 255, 0.95)'
                              : 'rgba(30, 41, 59, 0.95)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '12px',
                            minWidth: '200px',
                            boxShadow: theme.shadows[4]
                          }
                        }}
                      >
                        <DropdownMenuItem onClick={() => executeBulkAction('markAsSold')}>
                          <ListItemIcon>
                            <SellIcon sx={{ color: '#10b981' }} />
                          </ListItemIcon>
                          <ListItemText primary="Mark as Sold" />
                        </DropdownMenuItem>
                        
                        {/* <DropdownMenuItem onClick={() => executeBulkAction('markAsUnsold')}>
                          <ListItemIcon>
                            <UnsoldIcon sx={{ color: '#f59e0b' }} />
                          </ListItemIcon>
                          <ListItemText primary="Mark as Unsold" />
                        </DropdownMenuItem> */}
                        
                        <Divider />
                        
                        <DropdownMenuItem 
                          onClick={() => executeBulkAction('delete')}
                          sx={{
                            '&:hover': {
                              backgroundColor: alpha('#ef4444', 0.1)
                            }
                          }}
                        >
                          <ListItemIcon>
                            <DeleteSweepIcon sx={{ color: '#ef4444' }} />
                          </ListItemIcon>
                          <ListItemText primary="Delete All" sx={{ color: '#ef4444' }} />
                        </DropdownMenuItem>
                      </Menu>
                    </>
                  )}

                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    sx={{
                      ...(theme.palette.mode === 'light'
                        ? glassStyles.button.light.contained
                        : glassStyles.button.dark.contained),
                      background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.mode === 'dark' ? '#6366f1' : '#0284c7'} 100%)`,
                      borderRadius: '12px',
                      padding: '10px 20px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                    onClick={handleRefresh}
                  >
                    Refresh
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    sx={{
                      ...(theme.palette.mode === 'light'
                        ? glassStyles.button.light.contained
                        : glassStyles.button.dark.contained),
                      background: `linear-gradient(135deg, ${theme.palette.mode === 'light' ? '#8B5CF6' : '#A78BFA'} 0%, ${theme.palette.mode === 'light' ? '#7C3AED' : '#8B5CF6'} 100%)`,
                      borderRadius: '12px',
                      padding: '10px 20px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                    onClick={handleExport}
                  >
                    Export Data
                  </Button>
                </Box>
              </Box>

              {/* Category Sections */}
              {loading ? (
                <Box className="text-center py-12">
                  <CircularProgress sx={{ color: 'primary.main' }} />
                  <Typography className="mt-4" sx={{ color: 'text.secondary' }}>
                    Loading inventory data...
                  </Typography>
                </Box>
              ) : filteredData.length === 0 ? (
                <Box className="text-center py-12">
                  <Box
                    sx={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      background: `linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.1)} 0%, ${alpha(theme.palette.text.primary, 0.05)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.divider, 0.3)}`
                    }}
                  >
                    <SearchIcon sx={{ fontSize: '36px', color: 'text.disabled' }} />
                  </Box>
                  <Typography className="text-lg mb-4" sx={{ color: 'text.primary' }}>
                    No items found
                  </Typography>
                  <Typography className="mb-6" sx={{ color: 'text.secondary' }}>
                    Try adjusting your search or filters
                  </Typography>
                  <Button
                    variant="outlined"
                    sx={{
                      color: 'primary.main',
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderRadius: '12px',
                      padding: '8px 24px',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }}
                    onClick={handleClearFilters}
                  >
                    Clear All Filters
                  </Button>
                </Box>
              ) : (
                <Box className="space-y-4">
                  {Object.keys(filteredGroupedData).sort().map((category) => {
                    const items = filteredGroupedData[category];
                    const isExpanded = expandedCategories[category];

                    let categoryWeight = 0;
                    let categoryFine = 0;
                    let categorySold = 0;

                    for (let i = 0; i < items.length; i++) {
                      const item = items[i];
                      const weight = parseFloat(item.weight) || 0;
                      const melting = parseFloat(item.melting) || 0;
                      const wastage = parseFloat(item.wastage) || 0;

                      categoryWeight += weight;
                      categoryFine += weight * ((melting + wastage) / 100);
                      if (item.sold === "Yes") categorySold++;
                    }

                    return (
                      <Paper
                        key={category}
                        sx={{
                          ...glassStyles.card,
                          overflow: 'hidden',
                          borderRadius: '16px'
                        }}
                      >
                        <Box
                          className="flex items-center justify-between p-4 cursor-pointer"
                          onClick={() => handleCategoryToggle(category)}
                          sx={{
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        >
                          <Box className="flex items-center space-x-3">
                            <Box
                              sx={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                boxShadow: theme.shadows[2]
                              }}
                            >
                              {isExpanded ? (
                                <ExpandLessIcon sx={{ color: 'white' }} />
                              ) : (
                                <ExpandMoreIcon sx={{ color: 'white' }} />
                              )}
                            </Box>
                            <Box>
                              <Typography variant="h6" className="font-bold" sx={{ color: 'text.primary' }}>
                                {category}
                                {category !== 'Miscellaneous' &&
                                  ` (${getItemShortcut(category)})`}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {categorySold} sold • {items.length - categorySold} available
                              </Typography>
                            </Box>
                          </Box>
                          <Box className="flex items-center space-x-4">
                            <Chip
                              label={`${items.length} items`}
                              sx={{
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                fontWeight: 600,
                                borderRadius: '8px',
                                backdropFilter: 'blur(10px)'
                              }}
                            />
                            <Box className="text-right hidden md:block">
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Weight: <span className="font-bold" style={{ color: theme.palette.text.primary }}>
                                  {categoryWeight.toFixed(2)} gm
                                </span>
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Fine: <span className="font-bold" style={{ color: theme.palette.text.primary }}>
                                  {categoryFine.toFixed(2)} gm
                                </span>
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Collapse in={isExpanded}>
                          <Box className="p-4">
                            <div
                              className={`ag-theme-premium-${theme.palette.mode}`}
                              style={{
                                height: Math.min(400, Math.max(200, items.length * 50 + 50)),
                                width: '100%',
                                borderRadius: '12px',
                                overflow: 'hidden'
                              }}
                            >
                              <AgGridReact
                                rowData={items}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                rowSelection="multiple"
                                suppressRowClickSelection={false}
                                suppressCellFocus={true}
                                onGridReady={(params) => handleGridReady(category, params)}
                                onSelectionChanged={handleSelectionChanged(category, items)}
                                pagination={items.length > 50}
                                paginationPageSize={50}
                                domLayout="normal"
                                headerHeight={50}
                                rowHeight={50}
                                suppressScrollOnNewData={true}
                                animateRows={true}
                                rowMultiSelectWithClick={true}
                                suppressRowDeselection={false}
                              />
                            </div>
                          </Box>
                        </Collapse>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Edit Item Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCancelEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.mode === 'light'
              ? 'rgba(255, 255, 255, 0.95)'
              : 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(30px)',
            WebkitBackdropFilter: 'blur(30px)',
            border: '1px solid',
            borderColor: theme.palette.mode === 'light'
              ? 'rgba(255, 255, 255, 0.4)'
              : 'rgba(148, 163, 184, 0.2)',
            boxShadow: theme.shadows[6],
            borderRadius: '20px'
          }
        }}
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
        }}>
          Edit Item: {editingItem?.serialNo || editingItem?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                  Product Image
                </Typography>

                {editFormData.imageUrl ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      borderRadius: '12px',
                      background: alpha(theme.palette.primary.main, 0.05),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      flexWrap: { xs: 'wrap', sm: 'nowrap' }
                    }}
                  >
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        flexShrink: 0,
                        '&:hover': { opacity: 0.8 },
                        border: `1px solid ${alpha(theme.palette.divider, 0.3)}`
                      }}
                      onClick={() => window.open(editFormData.imageUrl, '_blank')}
                    >
                      <img
                        src={editFormData.imageUrl.replace('/upload/', '/upload/q_auto,f_auto/w_100,h_100,c_fill/')}
                        alt="Product"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                        Click image to view full size
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => {
                            const widget = window.cloudinary?.createUploadWidget(
                              {
                                cloudName: "dhllj9fhm",
                                uploadPreset: "bhairav_gold",
                                sources: ['local', 'camera', 'google_drive'],
                                multiple: false,
                                cropping: true,
                                croppingAspectRatio: 1,
                                folder: 'jewelry-items',
                                clientAllowedFormats: ['jpg', 'jpeg', 'png', 'webp'],
                                maxFileSize: 5000000,
                              },
                              (error, result) => {
                                if (error) {
                                  console.error('Upload error:', error);
                                  setImageUploadError('Failed to upload image');
                                  return;
                                }
                                if (result && result.event === 'success') {
                                  const imageUrl = result.info.secure_url;
                                  setEditFormData(prev => ({ ...prev, imageUrl }));
                                  setImageUploadError(null);
                                  setSnackbar({
                                    open: true,
                                    message: 'Image updated successfully',
                                    severity: 'success'
                                  });
                                }
                              }
                            );
                            widget?.open();
                          }}
                          sx={{ borderRadius: '8px' }}
                        >
                          Change Image
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            if (window.confirm('Are you sure you want to remove this image?')) {
                              setEditFormData(prev => ({ ...prev, imageUrl: '' }));
                              setSnackbar({
                                open: true,
                                message: 'Image removed. Save changes to update.',
                                severity: 'info'
                              });
                            }
                          }}
                          sx={{ borderRadius: '8px' }}
                        >
                          Remove Image
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      background: alpha(theme.palette.text.disabled, 0.05),
                      border: `1px dashed ${alpha(theme.palette.text.disabled, 0.3)}`,
                      textAlign: 'center'
                    }}
                  >
                    <ImageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      No image uploaded for this product
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={uploadingImage ? <CircularProgress size={20} /> : <AddIcon />}
                      onClick={() => {
                        setUploadingImage(true);
                        const widget = window.cloudinary?.createUploadWidget(
                          {
                            cloudName: "dhllj9fhm",
                            uploadPreset: "bhairav_gold",
                            sources: ['local', 'camera', 'google_drive'],
                            multiple: false,
                            cropping: true,
                            croppingAspectRatio: 1,
                            folder: 'jewelry-items',
                            clientAllowedFormats: ['jpg', 'jpeg', 'png', 'webp'],
                            maxFileSize: 5000000,
                          },
                          (error, result) => {
                            setUploadingImage(false);
                            if (error) {
                              console.error('Upload error:', error);
                              setImageUploadError('Failed to upload image');
                              setSnackbar({
                                open: true,
                                message: 'Failed to upload image. Please try again.',
                                severity: 'error'
                              });
                              return;
                            }
                            if (result && result.event === 'success') {
                              const imageUrl = result.info.secure_url;
                              setEditFormData(prev => ({ ...prev, imageUrl }));
                              setImageUploadError(null);
                              setSnackbar({
                                open: true,
                                message: 'Image added successfully! Save the changes to keep it.',
                                severity: 'success'
                              });
                            }
                          }
                        );
                        widget?.open();
                      }}
                      disabled={uploadingImage}
                      sx={{
                        borderRadius: '8px',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
                      }}
                    >
                      {uploadingImage ? 'Uploading...' : 'Add Image'}
                    </Button>
                    {imageUploadError && (
                      <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 1 }}>
                        {imageUploadError}
                      </Typography>
                    )}
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={editFormData.name || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                  size="small"
                  disabled
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Gross Weight (gm)"
                  type="text"
                  value={editFormData.grossWeight || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, grossWeight: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Less Weight (gm)"
                  type="text"
                  value={editFormData.lessWeight || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, lessWeight: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Weight (gm)"
                  type="text"
                  value={editFormData.weight || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, weight: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Metal"
                  value={editFormData.metal || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, metal: e.target.value }))}
                  size="small"
                  sx={{ mb: 2, width: '210px' }}
                >
                  {metalTypes.map((metal) => (
                    <MenuItem key={metal.id} value={metal.name}>
                      {metal.name} ({metal.shortcut})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="HUID"
                  value={editFormData.huid || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, huid: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={editFormData.sold || 'No'}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, sold: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="Yes">Sold</MenuItem>
                  <MenuItem value="No">Available</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Supplier Name"
                  value={editFormData.supplierName || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, supplierName: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Melting %"
                  type="text"
                  value={editFormData.melting || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, melting: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Buying Rate"
                  type="text"
                  value={editFormData.buyingRate || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, buyingRate: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Gold Rate"
                  type="text"
                  value={editFormData.goldCuttingRate || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, goldCuttingRate: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Wastage %"
                  type="text"
                  value={editFormData.wastage || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, wastage: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Making Charges %"
                  type="text"
                  value={editFormData.makingCharges || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, makingCharges: e.target.value }))}
                  size="small"
                  sx={{ mb: 2 }}
                />
              </Grid>

              {editFormData.metal === 'Silver' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Silver Rate"
                      type="text"
                      value={editFormData.silverRate || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, silverRate: e.target.value }))}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Labour"
                      type="text"
                      value={editFormData.labour || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, labour: e.target.value }))}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{
          background: alpha(theme.palette.text.primary, 0.05),
          padding: '16px 24px',
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`
        }}>
          <Button
            onClick={handleCancelEdit}
            sx={{
              color: 'text.secondary',
              borderColor: alpha(theme.palette.divider, 0.5),
              borderRadius: '12px',
              padding: '8px 20px',
              '&:hover': {
                backgroundColor: alpha(theme.palette.text.secondary, 0.1)
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleSave(editingItem?.id)}
            variant="contained"
            sx={{
              ...(theme.palette.mode === 'light'
                ? glassStyles.button.light.contained
                : glassStyles.button.dark.contained),
              borderRadius: '12px',
              padding: '8px 24px',
              fontWeight: 600,
              '&:hover': {
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            backdropFilter: 'blur(20px)',
            backgroundColor: alpha(
              snackbar.severity === 'success' ? theme.palette.success.main :
                snackbar.severity === 'error' ? theme.palette.error.main :
                  snackbar.severity === 'warning' ? theme.palette.warning.main :
                    theme.palette.info.main,
              0.9
            ),
            color: 'white',
            borderRadius: '12px',
            boxShadow: theme.shadows[4],
            border: '1px solid',
            borderColor: alpha(
              snackbar.severity === 'success' ? theme.palette.success.main :
                snackbar.severity === 'error' ? theme.palette.error.main :
                  snackbar.severity === 'warning' ? theme.palette.warning.main :
                    theme.palette.info.main,
              0.5
            )
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {isProcessingAction && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <CircularProgress size={60} sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ color: 'white', ml: 2 }}>
            Processing...
          </Typography>
        </Box>
      )}

      {/* Bulk Action Loading Overlay */}
      {bulkActionLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            flexDirection: 'column',
            gap: 2
          }}
        >
          <CircularProgress size={60} sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ color: 'white' }}>
            Processing bulk operation...
          </Typography>
          <Typography variant="body2" sx={{ color: alpha('#fff', 0.7) }}>
            Please wait while we update {selectedItems.length} item(s)
          </Typography>
        </Box>
      )}

      {/* Silver Transaction Modal - Split View */}
      <Dialog
        open={silverModalOpen}
        onClose={() => setSilverModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.mode === 'light'
              ? 'rgba(255, 255, 255, 0.95)'
              : 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(30px)',
            WebkitBackdropFilter: 'blur(30px)',
            overflow: 'hidden',
            maxHeight: '95vh'
          }
        }}
      >
        <DialogTitle sx={{
          ...(theme.palette.mode === 'light'
            ? glassStyles.button.light.contained
            : glassStyles.button.dark.contained),
          color: 'white',
          padding: '20px 24px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...(theme.palette.mode === 'light'
                    ? glassStyles.button.light.contained
                    : glassStyles.button.dark.contained),
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}>
                  <MoneyIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: 'white' }}>
                    Gross Stock Details
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    Track mix gold and silver stock additions and sales in real-time
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={() => setSilverModalOpen(false)}
                sx={{
                  color: 'white',
                  background: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.3)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{
            p: 3,
            background: alpha(theme.palette.background.paper, 0.8),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
              Metal Types Breakdown
            </Typography>
            <Paper sx={{
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: '12px',
              background: alpha(theme.palette.background.paper, 0.5)
            }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`
                    }}>
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary', textAlign: 'center', py: 1.5 }}>
                        Silver 999
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary', textAlign: 'center', py: 1.5 }}>
                        Silver 925
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary', textAlign: 'center', py: 1.5 }}>
                        Mix Silver
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary', textAlign: 'center', py: 1.5 }}>
                        Silver
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary', textAlign: 'center', py: 1.5 }}>
                        Old Silver
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, color: 'text.primary', textAlign: 'center', py: 1.5 }}>
                        Gold
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ textAlign: 'center', py: 2, fontWeight: 700, fontSize: '1.1rem', color: 'primary.main' }}>
                        {parseFloat(silverTypeStock?.["999silver"] || 0).toFixed(2)} gm
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', py: 2, fontWeight: 700, fontSize: '1.1rem', color: 'primary.main' }}>
                        {parseFloat(silverTypeStock?.["925silver"] || 0).toFixed(2)} gm
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', py: 2, fontWeight: 700, fontSize: '1.1rem', color: 'primary.main' }}>
                        {parseFloat(silverTypeStock?.["mixsilver"] || 0).toFixed(2)} gm
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', py: 2, fontWeight: 700, fontSize: '1.1rem', color: 'primary.main' }}>
                        {parseFloat(silverTypeStock?.["silver"] || 0).toFixed(2)} gm
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', py: 2, fontWeight: 700, fontSize: '1.1rem', color: 'primary.main' }}>
                        {parseFloat(silverTypeStock?.["oldsilver"] || 0).toFixed(2)} gm
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', py: 2, fontWeight: 700, fontSize: '1.1rem', color: 'primary.main' }}>
                        {parseFloat(silverTypeStock?.["gold"] || 0).toFixed(2)} gm
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
              Breakdown of metal stock by type
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            minHeight: '500px'
          }}>
            <Box sx={{
              flex: 1,
              borderRight: { lg: `1px solid ${alpha(theme.palette.divider, 0.2)}` },
              borderBottom: { xs: `1px solid ${alpha(theme.palette.divider, 0.2)}`, lg: 'none' }
            }}>
              <Box sx={{
                p: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.dark, 0.1)} 100%)`,
                borderBottom: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon sx={{ color: theme.palette.success.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Stock Added
                    </Typography>
                    <Chip
                      label={`${stockAddData.length} entries`}
                      size="small"
                      sx={{
                        background: alpha(theme.palette.success.main, 0.2),
                        color: theme.palette.success.main,
                        fontWeight: 600
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Sort by Date">
                      <IconButton
                        size="small"
                        onClick={() => handleSort('date')}
                        sx={{
                          color: sortConfig.key === 'date' ? theme.palette.success.main : 'text.secondary'
                        }}
                      >
                        <FilterListIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ height: '400px', overflow: 'auto', p: 2 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{
                        background: alpha(theme.palette.success.main, 0.05)
                      }}>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', width: '120px' }}>
                          Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                          Silver Type
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', width: '140px' }}>
                          Net Weight
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                          Supplier
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stockAddData.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{
                            '&:hover': {
                              background: alpha(theme.palette.success.main, 0.05)
                            }
                          }}
                        >
                          <TableCell sx={{ color: 'text.primary' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.date}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.silverType}
                              size="small"
                              sx={{
                                background: alpha(theme.palette.info.main, 0.1),
                                color: theme.palette.info.main,
                                fontWeight: 600,
                                fontSize: '0.75rem'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: 'text.primary', fontWeight: 700 }}>
                            {row.netWeight} gm
                          </TableCell>
                          <TableCell sx={{ color: 'text.primary' }}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: '150px' }}>
                              {row.supplierName}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {stockAddData.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <AddIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary">
                      No stock added yet
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box sx={{
                p: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.dark, 0.1)} 100%)`,
                borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RemoveIcon sx={{ color: theme.palette.error.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      Stock Sold
                    </Typography>
                    <Chip
                      label={`${stockSoldData.length} entries`}
                      size="small"
                      sx={{
                        background: alpha(theme.palette.error.main, 0.2),
                        color: theme.palette.error.main,
                        fontWeight: 600
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Sort by Date">
                      <IconButton
                        size="small"
                        onClick={() => handleSort('date')}
                        sx={{
                          color: sortConfig.key === 'date' ? theme.palette.error.main : 'text.secondary'
                        }}
                      >
                        <FilterListIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ height: '400px', overflow: 'auto', p: 2 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{
                        background: alpha(theme.palette.error.main, 0.05)
                      }}>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', width: '120px' }}>
                          Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                          Silver Type
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', width: '140px' }}>
                          Net Weight
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary', width: '140px' }}>
                          Bill No
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                          Customer
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.primary' }}>
                          Item
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stockSoldData.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{
                            '&:hover': {
                              background: alpha(theme.palette.error.main, 0.05)
                            }
                          }}
                        >
                          <TableCell sx={{ color: 'text.primary' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.date}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.silverType}
                              size="small"
                              sx={{
                                background: alpha(theme.palette.info.main, 0.1),
                                color: theme.palette.info.main,
                                fontWeight: 600,
                                fontSize: '0.75rem'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: 'text.primary', fontWeight: 700 }}>
                            {row.netWeight} gm
                          </TableCell>
                          <TableCell sx={{ color: 'text.primary' }}>
                            <Chip
                              label={row.billNo}
                              size="small"
                              variant="outlined"
                              sx={{
                                color: theme.palette.warning.main,
                                borderColor: theme.palette.warning.main,
                                fontSize: '0.75rem'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: 'text.primary' }}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: '120px' }}>
                              {row.customerName}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: 'text.primary' }}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: '150px' }}>
                              {row.itemName}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {stockSoldData.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <RemoveIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary">
                      No stock sold yet
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          p: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          background: alpha(theme.palette.background.paper, 0.5)
        }}>
          <Button
            onClick={() => setSilverModalOpen(false)}
            sx={{
              color: 'text.secondary',
              borderRadius: '10px',
              px: 3,
              '&:hover': {
                background: alpha(theme.palette.text.secondary, 0.1)
              }
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              borderRadius: '10px',
              px: 3,
              fontWeight: 600,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[4]
              },
              transition: 'all 0.3s ease'
            }}
            onClick={() => {
              setSilverModalOpen(false);
              handleRefresh();
            }}
          >
            Refresh Data
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryPage;